import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { TACTICS } from '../data/mitreAttack';

const useOffensiveStore = create(persist((set, get) => ({
  // Engagement state
  currentEngagement: null,
  engagements: [],
  mode: 'autonomous', // 'conversational' | 'autonomous'
  
  // Attack chain state
  activeTactic: null,
  tactics: TACTICS.map(t => ({
    ...t,
    status: 'pending', // 'pending' | 'queued' | 'active' | 'completed' | 'failed' | 'skipped'
    progress: 0,
    startTime: null,
    endTime: null
  })),
  
  // Techniques state
  techniques: [],
  activeTechnique: null,
  
  // Terminal state
  terminalOutputs: {}, // { techniqueId: { output: string, scrollToBottom: bool } }
  selectedTerminal: null,
  
  // Execution state
  isRunning: false,
  isPaused: false,
  activeFlowId: null,
  findings: [],
  
  // WebSocket state
  wsConnected: false,
  
  // Actions
  setMode: (mode) => set({ mode }),
  
  setCurrentEngagement: (engagement) => set({ currentEngagement: engagement }),
  
  addEngagement: (engagement) => set((state) => ({
    engagements: [...state.engagements, engagement]
  })),
  
  setActiveFlowId: (flowId) => set({ activeFlowId: flowId }),

  startEngagement: () => set({ isRunning: true, isPaused: false }),

  pauseEngagement: () => set({ isPaused: true }),

  stopEngagement: () => set({ isRunning: false, isPaused: false, activeFlowId: null }),
  
  // Tactic actions — supports both static MITRE tactics and dynamic PentAGI tasks
  setActiveTactic: (tacticId) => {
    set((state) => {
      const exists = state.tactics.some(t => t.id === tacticId);
      if (exists) {
        return {
          activeTactic: tacticId,
          tactics: state.tactics.map(t =>
            t.id === tacticId
              ? { ...t, status: 'active', startTime: new Date().toISOString() }
              : t
          ),
        };
      }
      // Dynamic tactic from PentAGI — add it to the list
      return {
        activeTactic: tacticId,
        tactics: [
          ...state.tactics,
          {
            id: tacticId,
            name: tacticId.replace(/^pentagi-task-/, 'Task '),
            shortName: `T${state.tactics.length + 1}`,
            status: 'active',
            progress: 0,
            startTime: new Date().toISOString(),
            endTime: null,
          },
        ],
      };
    });
  },
  
  updateTacticStatus: (tacticId, status, progress = null) => {
    set((state) => ({
      tactics: state.tactics.map(t => 
        t.id === tacticId 
          ? { 
              ...t, 
              status,
              progress: progress !== null ? progress : t.progress,
              endTime: status === 'completed' || status === 'failed' ? new Date().toISOString() : t.endTime
            }
          : t
      )
    }));
  },
  
  // Technique actions
  addTechnique: (technique) => {
    set((state) => {
      const id = technique.id || `${technique.techniqueId}-${Date.now()}`;
      if (state.techniques.some(t => t.id === id)) return state;
      return {
        techniques: [...state.techniques, {
          ...technique,
          id,
          status: technique.status || 'queued',
          progress: 0,
          output: '',
          findings: [],
          startTime: null,
          endTime: null
        }]
      };
    });
  },
  
  updateTechnique: (techniqueId, updates) => {
    set((state) => ({
      techniques: state.techniques.map(t => 
        t.id === techniqueId 
          ? { ...t, ...updates }
          : t
      )
    }));
  },
  
  setActiveTechnique: (techniqueId) => set({ activeTechnique: techniqueId }),
  
  removeTechnique: (techniqueId) => {
    set((state) => ({
      techniques: state.techniques.filter(t => t.id !== techniqueId)
    }));
  },
  
  // Terminal actions
  appendTerminalOutput: (techniqueId, output) => {
    set((state) => ({
      terminalOutputs: {
        ...state.terminalOutputs,
        [techniqueId]: {
          output: (state.terminalOutputs[techniqueId]?.output || '') + output,
          scrollToBottom: true
        }
      }
    }));
  },
  
  clearTerminalOutput: (techniqueId) => {
    set((state) => ({
      terminalOutputs: {
        ...state.terminalOutputs,
        [techniqueId]: { output: '', scrollToBottom: false }
      }
    }));
  },
  
  setSelectedTerminal: (techniqueId) => set({ selectedTerminal: techniqueId }),
  
  // Findings actions
  addFinding: (finding) => {
    set((state) => ({
      findings: [...state.findings, {
        ...finding,
        id: finding.id || `finding-${Date.now()}`,
        timestamp: new Date().toISOString()
      }]
    }));
  },
  
  // WebSocket actions
  setWsConnected: (connected) => set({ wsConnected: connected }),
  
  // Handle WebSocket events
  handleWsEvent: (event) => {
    const { type, data } = event;
    
    switch (type) {
      case 'tactic_started':
        get().setActiveTactic(data.tactic_id);
        break;
        
      case 'tactic_completed':
        get().updateTacticStatus(data.tactic_id, 'completed', 100);
        break;
        
      case 'technique_queued':
        get().addTechnique({
          id: data.technique_id,
          techniqueId: data.technique_id,
          tacticId: data.tactic_id,
          name: data.name,
          command: data.command,
          tool: data.tool,
          agent: data.agent,
          status: 'queued'
        });
        break;
        
      case 'technique_started':
        get().updateTechnique(data.technique_id, {
          status: 'running',
          startTime: data.timestamp
        });
        get().setActiveTechnique(data.technique_id);
        if (!get().selectedTerminal) {
          get().setSelectedTerminal(data.technique_id);
        }
        break;
        
      case 'technique_output':
        get().appendTerminalOutput(data.technique_id, data.output);
        break;
        
      case 'technique_completed':
        get().updateTechnique(data.technique_id, {
          status: data.status === 'success' ? 'completed' : 'failed',
          endTime: data.timestamp,
          progress: 100
        });
        
        // Add findings
        if (data.findings && data.findings.length > 0) {
          data.findings.forEach(finding => {
            get().addFinding({
              ...finding,
              techniqueId: data.technique_id,
              tacticId: data.tactic_id
            });
          });
        }
        break;
        
      case 'engagement_completed':
        get().stopEngagement();
        break;
        
      default:
        console.log('Unhandled event type:', type);
    }
  },
  
  // Reset state
  reset: () => set({
    activeTactic: null,
    tactics: TACTICS.map(t => ({
      ...t,
      status: 'pending',
      progress: 0,
      startTime: null,
      endTime: null
    })),
    techniques: [],
    activeTechnique: null,
    terminalOutputs: {},
    selectedTerminal: null,
    isRunning: false,
    isPaused: false,
    activeFlowId: null,
    findings: []
  })
}), {
  name: 'kingpin-offensive',
  partialize: (state) => ({
    activeFlowId: state.activeFlowId,
    isRunning: state.isRunning,
    currentEngagement: state.currentEngagement,
    mode: state.mode,
  }),
}));

export default useOffensiveStore;

